/**
 * EventParticipantCard — rollen-aware Teilnehmer-Card.
 *
 * Layout: Avatar mit RSVP-Badge links, Name + Personenzahl in der Mitte,
 * Aktion rechts (je Rolle: ⋯-Menue admin / Inline-Edit user-self / nichts guest).
 */

import { Check, Trash, Users, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { ActionMenu, type Action } from "@/components/ActionMenu";
import { useConfirm } from "@/components/Confirm";
import { InlineEdit } from "@/components/InlineEdit";
import { avatarColor, initials } from "@/lib/avatar";
import type { Participant } from "@/lib/api";
import styles from "./EventParticipantCard.module.css";

type AdminProps = {
  viewerRole: "admin";
  participant: Participant;
  onSave: (id: number, payload: { name?: string; participates?: boolean; persons?: number }) => void;
  onDelete: (id: number) => void;
};

type UserProps = {
  viewerRole: "user";
  participant: Participant;
  /** Wenn isSelf=true, ist die Card editable (eigene Daten). */
  isSelf: boolean;
  onSaveSelf?: (payload: { name?: string; participates?: boolean; persons?: number }) => void;
};

type GuestProps = {
  viewerRole: "guest";
  participant: Participant;
};

export type EventParticipantCardProps = AdminProps | UserProps | GuestProps;

export function EventParticipantCard(props: EventParticipantCardProps) {
  if (props.viewerRole === "admin") return <AdminPCard {...props} />;
  if (props.viewerRole === "user") return <UserPCard {...props} />;
  return <GuestPCard {...props} />;
}

// ---------------------------------------------------------------------------

function AdminPCard({ participant, onSave, onDelete }: AdminProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const handleDelete = async () => {
    const r = await confirm({
      title: t("admin.confirmDeleteParticipantTitle"),
      body: t("admin.confirmDeleteParticipantBody"),
      variant: "danger",
      confirmLabel: t("common.delete"),
    });
    if (r.confirmed) onDelete(participant.id);
  };

  const actions: Action[] = [
    {
      key: "delete",
      icon: Trash,
      label: t("common.delete"),
      onClick: handleDelete,
      variant: "danger",
    },
  ];

  const menuContent = (
    <>
      <InlineEdit
        label={t("admin.participantName")}
        value={participant.name}
        onSave={(next) => onSave(participant.id, { name: next })}
        maxLength={50}
        required
      />
    </>
  );

  return (
    <article className={`${styles.card} ${!participant.participates ? styles.cardDeclined : ""}`}>
      <Avatar participant={participant} />
      <div className={styles.body}>
        <span className={styles.name}>{participant.name}</span>
        <Meta participant={participant} />
      </div>
      <div className={styles.actions}>
        <RsvpToggle
          value={participant.participates}
          onChange={(v) => onSave(participant.id, { participates: v })}
        />
        <PersonsControl
          value={participant.persons}
          disabled={!participant.participates}
          onChange={(v) => onSave(participant.id, { persons: v })}
        />
        <ActionMenu
          triggerLabel={t("common.actionsForLabel", { name: participant.name })}
          title={t("common.actionsForLabel", { name: participant.name })}
          closeLabel={t("common.close")}
          actions={actions}
          content={menuContent}
        />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------

function UserPCard({ participant, isSelf, onSaveSelf }: UserProps) {
  if (!isSelf || !onSaveSelf) {
    return (
      <article className={`${styles.card} ${!participant.participates ? styles.cardDeclined : ""}`}>
        <Avatar participant={participant} />
        <div className={styles.body}>
          <span className={styles.name}>{participant.name}</span>
          <Meta participant={participant} />
        </div>
      </article>
    );
  }
  return (
    <article className={`${styles.card} ${styles.cardSelf} ${!participant.participates ? styles.cardDeclined : ""}`}>
      <Avatar participant={participant} />
      <div className={styles.body}>
        <span className={styles.name}>{participant.name}</span>
        <Meta participant={participant} />
      </div>
      <div className={styles.actions}>
        <RsvpToggle value={participant.participates} onChange={(v) => onSaveSelf({ participates: v })} />
        <PersonsControl
          value={participant.persons}
          disabled={!participant.participates}
          onChange={(v) => onSaveSelf({ persons: v })}
        />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------

function GuestPCard({ participant }: GuestProps) {
  return (
    <article className={`${styles.card} ${!participant.participates ? styles.cardDeclined : ""}`}>
      <Avatar participant={participant} />
      <div className={styles.body}>
        <span className={styles.name}>{participant.name}</span>
        <Meta participant={participant} />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------

function Avatar({ participant }: { participant: Participant }) {
  return (
    <span
      className={styles.avatar}
      style={{ backgroundColor: avatarColor(participant.name) }}
      aria-hidden
    >
      {initials(participant.name)}
      <span className={styles.rsvpBadge}>
        {participant.participates ? (
          <Check size={12} weight="bold" className={styles.rsvpYes} />
        ) : (
          <X size={12} weight="bold" className={styles.rsvpNo} />
        )}
      </span>
    </span>
  );
}

function Meta({ participant }: { participant: Participant }) {
  const { t } = useTranslation();
  return (
    <span className={styles.meta}>
      {participant.participates ? (
        <span className={styles.metaItem}>
          <Users size={14} weight="regular" />
          {participant.persons === 1
            ? t("admin.personsOne")
            : t("admin.personsOther", { count: participant.persons })}
        </span>
      ) : (
        <span className={styles.metaItem}>{t("admin.declined")}</span>
      )}
    </span>
  );
}

function RsvpToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`${styles.rsvpToggle} ${value ? styles.rsvpToggleYes : styles.rsvpToggleNo}`}
      onClick={() => onChange(!value)}
      aria-label={value ? t("user.rsvpYes") : t("user.rsvpNo")}
    >
      {value ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
      <span>{value ? t("user.rsvpYes") : t("user.rsvpNo")}</span>
    </button>
  );
}

function PersonsControl({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  if (disabled) return null;
  const dec = () => onChange(Math.max(1, value - 1));
  const inc = () => onChange(Math.min(10, value + 1));
  return (
    <div className={styles.personsControl} aria-label="Personen">
      <button type="button" onClick={dec} disabled={value <= 1}>
        −
      </button>
      <span>{value}</span>
      <button type="button" onClick={inc} disabled={value >= 10}>
        +
      </button>
    </div>
  );
}
